/**
 * Scope it → **Note** *type it* (Board 14 A) — the phone's full-screen
 * sheet (*Cancel · Note · Save*, the pattern of Board 16 E), where it is
 * saving to, and one box with Dictate in it. Saved through the upload queue
 * (§11.4) with a client id, so a note written with no signal goes up later
 * and a retry never makes two.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import type { RecordRef } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Dictation } from '../../components/dictation.js'
import { uploadQueue } from './upload-queue.js'
import { SavingTo, type SaveChip } from './saving-to.js'
import { useBackLayer } from '../use-activity-history.js'

const PAGE_LAYER = 4

export function NoteSheet({
  refs, chips, madeFromActivityId, batch, onClose,
}: {
  /** Every record it goes to, the first being where it is written. */
  refs: RecordRef[]
  chips: SaveChip[]
  madeFromActivityId?: string
  batch: string
  onClose: () => void
}) {
  /* The whole screen: back is its Cancel (Aaron, Oct 26). */
  useBackLayer(onClose)
  const [text, setText] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const save = async () => {
    const body = text.trim()
    if (!body) { setProblem('Type the note first, or Cancel.'); return }
    const [first, ...rest] = refs
    if (!first) { setProblem('Attach it to a job, a contact or a place first.'); return }
    await uploadQueue().addActivity({
      batch,
      record: first,
      payload: {
        kind: 'note', body, inputMode: 'typed', links: rest, expand: false,
        ...(madeFromActivityId ? { madeFromActivityId } : {}),
      },
      fileClientIds: [],
    })
    onClose()
  }

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-surface [line-height:normal]" data-overlay-layer={PAGE_LAYER}
      role="dialog" aria-modal="true" aria-label="Note">
      <div className="flex h-[calc(53px+env(safe-area-inset-top))] shrink-0 items-center gap-2 border-b border-line bg-surface px-3.5 pt-[env(safe-area-inset-top)]">
        <button type="button" onClick={onClose} className="w-[70px] text-left text-[15px] text-accent">Cancel</button>
        <span className="flex-1" />
        <b className="text-[15.5px]">Note</b>
        <span className="flex-1" />
        <button type="button" onClick={() => void save()} className="w-[70px] text-right text-[15px] font-semibold text-accent">
          Save
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <SavingTo chips={chips} />
        <div className={cn('mt-4 flex items-start gap-2 rounded-[10px] border border-line bg-surface px-3 py-2.5',
          'focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]')}>
          <textarea
            autoFocus
            value={text}
            onChange={(event) => { setText(event.target.value); setProblem(null) }}
            rows={7}
            placeholder="Add a note"
            aria-label="Note"
            className="min-w-0 flex-1 resize-none bg-transparent text-[14.5px] leading-[1.5] outline-none focus-visible:shadow-none"
          />
          <Dictation variant="icon" className="mt-0.5"
            onText={(said) => setText((was) => (was ? `${was} ${said}` : said))} />
        </div>
        {problem && <p role="alert" className="mt-2.5 text-[12.5px] font-semibold text-danger">{problem}</p>}
      </div>
    </div>,
    document.body,
  )
}
